<template>
  <section class="page-panel fleet-calendar">
    <v-progress-linear
      v-if="calendarState.isLoading"
      class="fleet-calendar__progress"
      indeterminate
    />

    <div class="fleet-calendar__header grid grid-gap-16 grid-gap-sm-0 align-items-center">
      <h2 class="col-12 col-sm-6">Kalendar zauzetosti</h2>

      <ToolbarActionButton
        class="col-12 col-sm-6 justify-self-sm-end"
        text="Nova rezervacija"
        :disabled="!hasNewReservationPage"
        :to="hasNewReservationPage ? { name: 'fleet-reservation-create', query: { month: selectedMonthValue } } : null"
      />
    </div>

    <div class="fleet-calendar__toolbar">
      <Filters
        v-model="calendarState.filters"
        class="fleet-calendar__brand-filter"
        :filter-fields="brandFilterFields"
        @change="loadCalendarAsync"
      />

      <div class="fleet-calendar__month-navigation">
        <v-btn
          aria-label="Prethodni mjesec"
          class="button button--icon fleet-calendar__month-button"
          icon="mdi-chevron-left"
          size="small"
          variant="text"
          @click="setMonth(-1)"
        />
        <strong class="fleet-calendar__month">{{ selectedMonthLabel }}</strong>
        <v-btn
          aria-label="Sljedeći mjesec"
          class="button button--icon fleet-calendar__month-button"
          icon="mdi-chevron-right"
          size="small"
          variant="text"
          @click="setMonth(1)"
        />
      </div>
    </div>

    <v-alert
      v-if="calendarState.errorMessage"
      closable
      type="error"
      variant="tonal"
      @click:close="calendarState.errorMessage = null"
    >
      {{ calendarState.errorMessage }}
    </v-alert>

    <v-empty-state
      v-else-if="!calendarState.isLoading && !vehicleGroups.length"
      icon="mdi-car-off"
      text="Za odabrani mjesec i marku nema vozila."
      title="Nema vozila"
    />

    <div
      v-else
      class="fleet-calendar__scroll"
    >
      <div
        class="fleet-calendar__matrix"
        :style="{ '--calendar-days': calendarDays.length }"
      >
        <div class="fleet-calendar__row fleet-calendar__row--days">
          <div class="fleet-calendar__vehicle-heading">Vozni park</div>

          <div class="fleet-calendar__days">
            <div
              v-for="calendarDay in calendarDays"
              :key="calendarDay.isoDate"
              class="fleet-calendar__day"
              :class="{ 'fleet-calendar__day--weekend': calendarDay.isWeekend }"
            >
              <strong>{{ calendarDay.day }}</strong>
              <span>{{ calendarDay.weekday }}</span>
            </div>
          </div>
        </div>

        <section
          v-for="vehicleGroup in vehicleGroups"
          :key="vehicleGroup.key"
          class="fleet-calendar__group"
        >
          <header class="fleet-calendar__group-header">
            <div
              class="fleet-calendar__group-label"
              :class="{ 'fleet-calendar__group-label--events': vehicleGroup.key === 'events' }"
            >
              <span class="fleet-calendar__group-name">{{ vehicleGroup.label }}</span>
              <span class="fleet-calendar__group-count">{{ getVehicleCountLabel(vehicleGroup.vehicles.length) }}</span>
            </div>
          </header>

          <div
            v-for="vehicle in vehicleGroup.vehicles"
            :key="vehicle.id"
            class="fleet-calendar__row fleet-calendar__row--vehicle"
          >
            <div class="fleet-calendar__vehicle">
              <strong>{{ getEventBrandName(vehicle.brandCode) }} {{ vehicle.name }}</strong>
              <span>{{ vehicle.plate }}</span>

              <v-tooltip
                content-class="fleet-calendar__vehicle-tooltip"
                location="top"
                :model-value="calendarState.activeVehicleInfoId === vehicle.id"
                :offset="12"
                :open-on-hover="false"
                open-on-click
                @update:model-value="setVehicleInfoOpen(vehicle.id, $event)"
              >
                <template #activator="{ props: tooltipProps }">
                  <v-btn
                    v-bind="tooltipProps"
                    :aria-label="`Informacije o vozilu ${vehicle.plate}`"
                    class="button button--icon fleet-calendar__info-button"
                    :class="{ 'fleet-calendar__info-button--open': calendarState.activeVehicleInfoId === vehicle.id }"
                    icon
                    size="x-small"
                    variant="text"
                  >
                    <svg
                      aria-hidden="true"
                      fill="none"
                      height="12"
                      stroke="currentColor"
                      stroke-width="1.167"
                      viewBox="0 0 12 12"
                      width="12"
                    >
                      <circle cx="6" cy="6" r="5.25" />
                      <circle cx="6" cy="3.5" fill="currentColor" r="0.58" stroke="none" />
                      <path d="M6 5.25v3.25" stroke-linecap="round" />
                    </svg>
                  </v-btn>
                </template>

                <div class="fleet-calendar__vehicle-tooltip-text">
                  <div>Šasija: {{ vehicle.vin }}</div>
                  <div>Boja: {{ vehicle.color }}</div>
                </div>
              </v-tooltip>
            </div>

            <div class="fleet-calendar__timeline">
              <div class="fleet-calendar__day-backgrounds">
                <button
                  v-for="calendarDay in calendarDays"
                  :key="calendarDay.isoDate"
                  :aria-label="`Nova rezervacija za ${vehicle.plate}, ${formatDate(calendarDay.isoDate)}`"
                  class="fleet-calendar__day-background"
                  :class="{ 'fleet-calendar__day-background--weekend': calendarDay.isWeekend }"
                  :disabled="vehicle.isArchived || !hasNewReservationPage || isDayBooked(vehicle, calendarDay.isoDate)"
                  type="button"
                  @click="openNewReservation(vehicle.id, calendarDay.isoDate)"
                />
              </div>

              <template
                v-for="period in getVisiblePeriods(vehicle)"
                :key="period.id"
              >
                <v-menu
                  v-if="period.type === 'reservation'"
                  content-class="table-menu"
                  location="bottom start"
                  :offset="[20, 28]"
                  :target="calendarState.reservationMenuTarget"
                  @update:model-value="setReservationMenuOpen(period.id, $event)"
                >
                  <template #activator="{ props: menuProps }">
                    <v-tooltip
                      content-class="fleet-calendar__vehicle-tooltip fleet-calendar__period-tooltip"
                      :disabled="isMobile || calendarState.activeReservationMenuId === period.id"
                      location="top"
                      :offset="12"
                      :open-on-focus="false"
                    >
                      <template #activator="{ props: tooltipProps }">
                        <button
                          v-bind="mergeProps(menuProps, tooltipProps)"
                          :aria-label="`Akcije rezervacije ${period.label}, ${formatPeriod(period)}`"
                          class="fleet-calendar__period fleet-calendar__period--reservation"
                          :style="getPeriodStyle(period)"
                          type="button"
                          @click.capture="setReservationMenuTarget($event)"
                        >
                          <span class="fleet-calendar__period-label">{{ period.label }}</span>
                          <v-icon
                            class="fleet-calendar__period-menu-icon"
                            icon="mdi-dots-horizontal"
                            size="16"
                          />
                        </button>
                      </template>

                      <div class="fleet-calendar__vehicle-tooltip-text">{{ period.label }}</div>
                    </v-tooltip>
                  </template>

                  <div class="table-menu__content grid-stack grid-gap-16">
                    <v-btn
                      class="button button--menu fleet-calendar__menu-action"
                      :to="{
                        name: 'fleet-reservation-edit',
                        params: { reservationId: period.id },
                        query: { month: selectedMonthValue },
                      }"
                      variant="text"
                    >
                      Uredi rezervaciju
                    </v-btn>
                    <v-btn
                      v-if="!period.recordId"
                      class="button button--menu fleet-calendar__menu-action"
                      :to="{
                        name: 'fleet-record-pickup-create',
                        query: { reservationId: period.id, from: 'calendar', month: selectedMonthValue },
                      }"
                      variant="text"
                    >
                      Otvori zapisnik o preuzimanju
                    </v-btn>
                    <v-btn
                      class="button button--menu button--text-danger table-menu__button--danger"
                      variant="text"
                      @click="openDeleteReservationDialog(period, vehicle)"
                    >
                      <v-icon class="table-menu__icon" icon="mdi-trash-can-outline" size="16" />
                      Ukloni rezervaciju
                    </v-btn>
                  </div>
                </v-menu>

                <v-tooltip
                  v-else
                  content-class="fleet-calendar__vehicle-tooltip fleet-calendar__period-tooltip"
                  :disabled="isMobile"
                  location="top"
                  :offset="12"
                  :open-on-focus="false"
                >
                  <template #activator="{ props: tooltipProps }">
                    <span
                      v-bind="tooltipProps"
                      :aria-label="`${period.label}, ${formatPeriod(period)}`"
                      class="fleet-calendar__period"
                      :class="`fleet-calendar__period--${period.type}`"
                      :style="getPeriodStyle(period)"
                    >
                      <span class="fleet-calendar__period-label">{{ period.label }}</span>
                    </span>
                  </template>

                  <div class="fleet-calendar__vehicle-tooltip-text">{{ period.label }}</div>
                </v-tooltip>
              </template>
            </div>
          </div>
        </section>
      </div>
    </div>

    <AppConfirmDialog
      v-model="calendarState.deleteDialogOpen"
      :title="calendarState.selectedReservation?.confirmDraftRecordDeletion ? 'Ukloni rezervaciju i Draft?' : 'Ukloni rezervaciju?'"
      :subtitle="selectedReservationSubtitle"
      :message="calendarState.selectedReservation?.confirmDraftRecordDeletion ? 'Rezervacija ima Draft zapisnik. Brisanjem rezervacije izbrisat će se i Draft zapisnik. Želite li nastaviti?' : 'Jeste li sigurni da želite ukloniti ovu rezervaciju?'"
      :primary-text="calendarState.selectedReservation?.confirmDraftRecordDeletion ? 'Ukloni oboje' : 'Ukloni'"
      secondary-text="Odustani"
      :loading="calendarState.isDeletingReservation"
      :close-on-confirm="false"
      @close="calendarState.selectedReservation = null"
      @confirm="deleteReservationAsync"
    />
  </section>
</template>

<script setup>
import { computed, mergeProps, reactive, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { useDisplay } from 'vuetify'

import AppConfirmDialog from '../../components/AppConfirmDialog.vue'
import Filters from '../../components/Filters.vue'
import ToolbarActionButton from '../../components/ToolbarActionButton.vue'
import { formatDate, getEventBrandName, getVehicleBrandOptions, vehiclePurposeOptions } from '../../helpers'
import { apiClient } from '../../services/apiClient'
import { showErrorToast, showRequestErrorToast, showSuccessToast } from '../../store/appStore'

const dayWidth = 44
const route = useRoute()
const router = useRouter()
const hasNewReservationPage = router.hasRoute('fleet-reservation-create')
const { mobile: isMobile } = useDisplay({ mobileBreakpoint: 768 })
let latestCalendarRequestId = 0

const calendarState = reactive({
  activeReservationMenuId: null,
  activeVehicleInfoId: null,
  deleteDialogOpen: false,
  errorMessage: null,
  filters: {
    brandCode: null,
  },
  isDeletingReservation: false,
  isLoading: false,
  reservationMenuTarget: null,
  selectedReservation: null,
  vehicles: [],
})

const brandFilterFields = [{
  key: 'brandCode',
  type: 'select',
  placeholder: 'SVE MARKE',
  items: getVehicleBrandOptions(),
  width: '180px',
}]

const selectedMonth = computed(() => {
  const month = route.query.month

  if (typeof month === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(month)) {
    return new Date(Number(month.slice(0, 4)), Number(month.slice(5, 7)) - 1, 1)
  }

  const today = new Date()
  return new Date(today.getFullYear(), today.getMonth(), 1)
})
const selectedMonthValue = computed(() => {
  const year = selectedMonth.value.getFullYear()
  const month = String(selectedMonth.value.getMonth() + 1).padStart(2, '0')

  return `${year}-${month}`
})
const selectedMonthLabel = computed(() => {
  const monthLabel = new Intl.DateTimeFormat('hr-HR', {
    month: 'long',
    year: 'numeric',
  }).format(selectedMonth.value).replace('.', '')

  return monthLabel.charAt(0).toUpperCase() + monthLabel.slice(1)
})
const calendarDays = computed(() => {
  const year = selectedMonth.value.getFullYear()
  const month = selectedMonth.value.getMonth()
  const daysCount = new Date(year, month + 1, 0).getDate()

  return Array.from({ length: daysCount }, (_, dayIndex) => {
    const date = new Date(year, month, dayIndex + 1)

    return {
      day: dayIndex + 1,
      isoDate: formatLocalDate(date),
      isWeekend: date.getDay() === 0 || date.getDay() === 6,
      weekday: new Intl.DateTimeFormat('hr-HR', { weekday: 'short' })
        .format(date)
        .replace('.', '')
        .toUpperCase(),
    }
  })
})
const vehicleGroups = computed(() => {
  const fleetPurposes = [...new Set(calendarState.vehicles
    .filter((vehicle) => vehicle.isForDrive)
    .map((vehicle) => vehicle.purpose))]
  const groups = fleetPurposes.map((purpose) => ({
    key: `fleet-${purpose}`,
    label: vehiclePurposeOptions.find(({ value }) => value === purpose)?.title ?? purpose,
    vehicles: calendarState.vehicles.filter((vehicle) => vehicle.isForDrive && vehicle.purpose === purpose),
  }))
  const eventVehicles = calendarState.vehicles.filter((vehicle) => vehicle.isForEvent && !vehicle.isForDrive)

  if (eventVehicles.length) {
    groups.push({
      key: 'events',
      label: 'Događaji',
      vehicles: eventVehicles,
    })
  }

  return groups
})
const selectedReservationSubtitle = computed(() => {
  const selection = calendarState.selectedReservation

  if (!selection) {
    return ''
  }

  const { period, vehicle } = selection
  return `${period.label} · ${getEventBrandName(vehicle.brandCode)} ${vehicle.name} · ${vehicle.plate}`
})

watch(selectedMonthValue, loadCalendarAsync, { immediate: true })

async function loadCalendarAsync () {
  const requestId = ++latestCalendarRequestId
  calendarState.activeVehicleInfoId = null
  calendarState.errorMessage = null
  calendarState.isLoading = true

  try {
    const calendarResponse = await apiClient.getAsync('/api/drivepark/calendar', {
      queryParams: {
        brandCode: calendarState.filters.brandCode,
        month: selectedMonthValue.value,
      },
    })

    if (requestId === latestCalendarRequestId) {
      calendarState.vehicles = calendarResponse.vehicles
    }
  } catch (error) {
    console.error('Failed to load the fleet calendar.', error)

    if (requestId === latestCalendarRequestId) {
      calendarState.vehicles = []
      calendarState.errorMessage = 'Kalendar zauzetosti nije bilo moguće učitati.'
    }
  } finally {
    if (requestId === latestCalendarRequestId) {
      calendarState.isLoading = false
    }
  }
}

function setMonth (monthOffset) {
  const month = new Date(
    selectedMonth.value.getFullYear(),
    selectedMonth.value.getMonth() + monthOffset,
    1
  )

  router.push({ name: 'fleet-calendar', query: { month: formatMonth(month) } })
}

function getVisiblePeriods (vehicle) {
  const monthStart = calendarDays.value[0].isoDate
  const monthEnd = calendarDays.value[calendarDays.value.length - 1].isoDate
  const periods = [...vehicle.entries]

  if (vehicle.isActive === false) {
    const today = new Date().toLocaleDateString('sv-SE', { timeZone: 'Europe/Zagreb' })
    const startDate = today > monthStart ? today : monthStart

    if (startDate <= monthEnd) {
      periods.push({
        id: `unavailable-${vehicle.id}`,
        type: 'unavailable',
        label: 'Neispravno',
        startDate,
        endDate: monthEnd,
      })
    }
  }

  return periods.filter((period) => period.startDate <= monthEnd && period.endDate >= monthStart)
}

function isDayBooked (vehicle, date) {
  return vehicle.entries.some((entry) => entry.startDate <= date && entry.endDate >= date)
}

function getPeriodStyle (period) {
  const monthStart = calendarDays.value[0].isoDate
  const monthEnd = calendarDays.value[calendarDays.value.length - 1].isoDate
  const visibleStart = period.startDate < monthStart ? monthStart : period.startDate
  const visibleEnd = period.endDate > monthEnd ? monthEnd : period.endDate
  const startDay = Number(visibleStart.slice(-2))
  const endDay = Number(visibleEnd.slice(-2))

  return {
    left: `${(startDay - 1) * dayWidth + 4}px`,
    width: `${(endDay - startDay + 1) * dayWidth - 8}px`,
  }
}

function formatPeriod (period) {
  return `${formatDate(period.startDate)} – ${formatDate(period.endDate)}`
}

function formatLocalDate (date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')

  return `${year}-${month}-${day}`
}

function formatMonth (date) {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
}

function getVehicleCountLabel (count) {
  return `${count} ${count % 100 !== 11 && count % 10 === 1 ? 'vozilo' : 'vozila'}`
}

function setVehicleInfoOpen (vehicleId, isOpen) {
  if (isOpen) {
    calendarState.activeVehicleInfoId = vehicleId
  } else if (calendarState.activeVehicleInfoId === vehicleId) {
    calendarState.activeVehicleInfoId = null
  }
}

function setReservationMenuOpen (reservationId, isOpen) {
  if (isOpen) {
    calendarState.activeReservationMenuId = reservationId
  } else if (calendarState.activeReservationMenuId === reservationId) {
    calendarState.activeReservationMenuId = null
  }
}

function setReservationMenuTarget (event) {
  calendarState.reservationMenuTarget = event.currentTarget.querySelector('.fleet-calendar__period-menu-icon')
}

function openNewReservation (vehicleId, startDate) {
  router.push({
    name: 'fleet-reservation-create',
    query: { month: selectedMonthValue.value, vehicleId, startDate },
  })
}

function openDeleteReservationDialog (period, vehicle) {
  calendarState.selectedReservation = { period, vehicle, confirmDraftRecordDeletion: false }
  calendarState.deleteDialogOpen = true
}

async function deleteReservationAsync () {
  if (!calendarState.selectedReservation || calendarState.isDeletingReservation) {
    return
  }

  calendarState.isDeletingReservation = true

  try {
    await apiClient.deleteAsync(
      `/api/drivepark/reservations/${calendarState.selectedReservation.period.id}`,
      { queryParams: { confirmDraftRecordDelete: calendarState.selectedReservation.confirmDraftRecordDeletion } }
    )
    calendarState.deleteDialogOpen = false
    showSuccessToast('Rezervacija je uspješno uklonjena.')
    await loadCalendarAsync()
  } catch (error) {
    console.error('Failed to delete the drive park reservation.', error)
    if (error.status === 409 && error.data?.error === 'reservation_has_draft_record') {
      calendarState.selectedReservation.confirmDraftRecordDeletion = true
    } else if (error.status === 409 && error.data?.error === 'record_not_draft') {
      calendarState.deleteDialogOpen = false
      showErrorToast('Rezervaciju nije moguće ukloniti jer je povezani zapisnik već dovršen.')
    } else if (error.status === 404) {
      calendarState.deleteDialogOpen = false
      await loadCalendarAsync()
    } else {
      showRequestErrorToast(error, 'Rezervaciju nije bilo moguće ukloniti.')
    }
  } finally {
    calendarState.isDeletingReservation = false
  }
}
</script>

<style
  scoped
  lang="scss"
>
.fleet-calendar {
  --calendar-day-width: 44px;
  --calendar-vehicle-width: 228px;

  position: relative;

  &__progress {
    position: absolute;
    z-index: 2;
    top: 0;
    left: 0;
    border-radius: var(--page-panel-radius) var(--page-panel-radius) 0 0;
  }

  &__header {
    margin-bottom: 18px;
    padding-bottom: 18px;
    border-bottom: 1px solid #d9dce0;
  }

  &__toolbar {
    display: flex;
    flex-direction: column;
    gap: 16px;
    margin-bottom: 18px;

    @include breakpoint-up('sm') {
      align-items: center;
      flex-direction: row;
    }
  }

  &__brand-filter {
    :deep(.select--filter) {
      --select-font-size: 14px;
    }

    :deep(.select--filter .v-field__input) {
      line-height: 22px;
    }

    @include breakpoint-up('sm') {
      flex: 0 0 180px;
      width: 180px;
    }
  }

  &__month-navigation {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 10px;
    min-width: 0;

    @include breakpoint-up('sm') {
      flex: 1;
      margin-right: 180px;
    }
  }

  &__month {
    min-width: 150px;
    color: var(--color-dialog-title);
    font-size: 22px;
    font-weight: 600;
    line-height: 30px;
    text-align: center;
  }

  &__month-button.v-btn {
    width: 30px;
    height: 30px;
    background: var(--color-page-background);
    color: var(--color-text-muted);
  }

  &__scroll {
    overflow-x: auto;
    margin-top: 18px;
    border: 1px solid var(--color-border);
    border-radius: 6px;
    scrollbar-color: #bababa transparent;
    scrollbar-width: thin;

    &::-webkit-scrollbar {
      height: 8px;
    }

    &::-webkit-scrollbar-track {
      background: transparent;
    }

    &::-webkit-scrollbar-thumb {
      border-radius: 4px;
      background: #bababa;
    }
  }

  &__matrix {
    min-width: calc(var(--calendar-vehicle-width) + (var(--calendar-days) * var(--calendar-day-width)));
  }

  &__row {
    display: flex;

    &--days {
      border-bottom: 1px solid var(--color-border);
    }

    &--vehicle {
      min-height: 52px;
      border-bottom: 1px solid var(--color-border-soft);
    }
  }

  &__row--vehicle:last-child {
    border-bottom: 0;
  }

  &__vehicle-heading,
  &__vehicle {
    position: sticky;
    z-index: 1;
    left: 0;
    flex: 0 0 var(--calendar-vehicle-width);
    width: var(--calendar-vehicle-width);
  }

  &__vehicle-heading {
    display: flex;
    align-items: flex-end;
    min-height: 72px;
    padding: 16px 12px;
    border-right: 1px solid var(--color-border-soft);
    background: #d1d1d1;
  }

  &__vehicle-heading,
  &__group-name {
    color: #2c2d2d;
    font-size: 11px;
    font-weight: 600;
    line-height: 17px;
    letter-spacing: 0.06em;
    text-transform: uppercase;
  }

  &__vehicle {
    display: flex;
    justify-content: center;
    flex-direction: column;
    padding: 8px 36px 8px 12px;
    background: var(--color-white);

    &:hover .fleet-calendar__info-button {
      opacity: 1;
    }

    > strong,
    > span {
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    > strong {
      color: var(--color-dialog-title);
      font-size: 13px;
      font-weight: 500;
      line-height: 17px;
    }

    > span {
      color: var(--color-dialog-subtitle);
      font-size: 11px;
      line-height: 14px;
      letter-spacing: 0.02em;
    }
  }

  &__info-button.v-btn {
    position: absolute;
    top: 50%;
    right: 4px;
    transform: translateY(-50%);
    color: var(--color-dialog-subtitle);
    opacity: 0;

    &:focus-visible {
      opacity: 1;
    }

    @media (hover: none) {
      opacity: 1;
    }
  }

  &__info-button--open.v-btn {
    opacity: 1;
  }

  &__days,
  &__day-backgrounds {
    display: flex;
  }

  &__day {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    flex: 0 0 var(--calendar-day-width);
    flex-direction: column;
    height: 72px;
    padding-bottom: 8px;
    border-right: 1px solid var(--color-border-soft);

    strong {
      color: var(--color-dialog-title);
      font-size: 14px;
      font-weight: 600;
      line-height: 14px;
    }

    span {
      color: var(--color-dialog-subtitle);
      font-size: 10px;
      font-weight: 500;
      line-height: 15px;
      letter-spacing: 0.04em;
      text-transform: uppercase;
    }
  }

  &__day--weekend {
    strong {
      color: #4f46e5;
    }

    span {
      color: #818cf8;
    }
  }

  &__group-header {
    background: #f8fafc;
  }

  &__group-label {
    position: sticky;
    z-index: 1;
    left: 0;
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: var(--calendar-vehicle-width);
    min-height: 34px;
    padding: 8px 12px;
    border-right: 1px solid var(--color-border-soft);
    background: #f8fafc;

    &--events {
      background: #d1d1d1;
    }
  }

  &__group-count {
    color: var(--color-dialog-subtitle);
    font-size: 10px;
    white-space: nowrap;
  }

  &__timeline {
    position: relative;
    z-index: 0;
    flex: 0 0 calc(var(--calendar-days) * var(--calendar-day-width));
  }

  &__day-backgrounds {
    position: absolute;
    inset: 0;
  }

  &__day-background {
    flex: 0 0 var(--calendar-day-width);
    padding: 0;
    border: 0;
    border-left: 1px solid var(--color-border-soft);
    background: transparent;
    cursor: pointer;

    &:disabled {
      cursor: default;
    }
  }

  &__day--weekend,
  &__day-background--weekend {
    background: rgba(238, 242, 255, 0.5);
  }

  &__period {
    position: absolute;
    z-index: 1;
    top: 12px;
    display: flex;
    align-items: center;
    overflow: hidden;
    height: 28px;
    padding: 0 8px;
    border: 0;
    border-radius: 5px;
    color: var(--color-white);
    font-family: var(--font-family);
    font-size: 12px;
    font-weight: 500;
    line-height: 14px;
    text-align: left;
    white-space: nowrap;

    &--reservation {
      background: #5146e5;
      cursor: pointer;
    }

    &--event {
      background: #379724;
    }

    &--unavailable {
      background: #918787;
    }
  }

  &__period--reservation:hover .fleet-calendar__period-menu-icon,
  &__period--reservation:focus-visible .fleet-calendar__period-menu-icon {
    opacity: 1;
  }

  &__period-label {
    overflow: hidden;
    flex: 1;
    text-overflow: ellipsis;
  }

  &__period-menu-icon {
    flex: 0 0 auto;
    opacity: 0;

    @media (hover: none) {
      opacity: 1;
    }
  }

  &__menu-action.v-btn {
    color: var(--color-menu-text);
    font-size: 13px;
    line-height: 20px;
  }

  &__menu-action.v-btn.v-btn--disabled {
    opacity: 1;
  }

  &__vehicle-tooltip-text {
    flex: 1 0 auto;
    max-width: 272px;
    color: var(--color-white);
    font-family: var(--font-family);
    font-size: 14px;
    line-height: 21px;
  }
}

:global(.v-tooltip > .fleet-calendar__vehicle-tooltip.v-overlay__content) {
  display: flex;
  align-items: center;
  justify-content: center;
  max-width: 272px;
  min-height: 64px;
  padding: 8px;
  border-radius: 5px;
  background: #313d3e;
}

:global(.v-tooltip > .fleet-calendar__vehicle-tooltip.fleet-calendar__period-tooltip.v-overlay__content) {
  min-height: 0;
}

:global(.v-tooltip > .fleet-calendar__vehicle-tooltip.v-overlay__content::after) {
  position: absolute;
  top: 100%;
  left: 50%;
  border: 6px solid transparent;
  border-top-color: #313d3e;
  content: '';
  transform: translateX(-50%);
}
</style>
